/**
 * English: Developers
 *
 * What this file covers: API keys, webhooks and the developer pages.
 *
 * Tone: Technical and exact. Keep code, field names, HTTP terms and product names untranslated.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const developers: Catalog = {
  'developers.title': 'Developers',
  'developers.description': 'The API your own systems can call, with an API key you create yourself.',
  'developers.versionBadge': 'Version {{version}}, {{date}}',
  'developers.copyNamed': 'Copy the {{name}}',
  'developers.tab.start': 'Getting started',
  'developers.tab.reference': 'Endpoints',
  'developers.tab.webhooks': 'Events we send you',
  'developers.tab.changelog': 'Changelog',
  'developers.download.button': 'Download',
  'developers.download.current': 'Current version',
  'developers.download.version': 'Version {{version}}',
  'developers.download.failed': 'The document could not be downloaded.',
  'developers.loadError.title': 'The documentation could not be loaded',
  'developers.loadError.body': 'This page is built from the live API, so it needs the platform to answer. Try again in a moment.',
  'developers.key.title': 'Your API key',
  'developers.key.label': 'Paste a key to use in the examples',
  'developers.key.hint': 'Kept in this browser tab only, for as long as it is open. It is never saved, never sent anywhere except the API itself, and it is gone when you close the tab. Leave it empty and the examples show a placeholder instead.',
  'developers.key.wrongPrefix': 'A RepletoryReach key starts with rk_live_.',
  'developers.key.inUse': 'In use in the examples on this page.',
  'developers.key.forget': 'Forget it',
  'developers.key.manage': 'Create or revoke keys',
  'developers.start.step1.title': '1. Create a key',
  'developers.start.step1.body': 'Keys are made in Settings, under API keys. Choose only the scopes your integration needs: a key cannot be widened later, so a narrow key is one less thing to worry about if it ever leaks.',
  'developers.start.step1.link': 'Go to API keys',
  'developers.start.step1.once': 'The key is shown once, at the moment you create it. Save it somewhere safe. If you lose it, revoke it and make another.',
  'developers.start.step2.title': '2. Address and header',
  'developers.start.step2.body': 'Every request goes to the same address and carries the key in the same header.',
  'developers.start.baseUrl': 'Base address',
  'developers.start.header': 'Header',
  'developers.start.step2.serverSide': 'Send the key from your own server. A key in a web page or a mobile app is a key every user of it can read.',
  'developers.start.step3.title': '3. Your first request',
  'developers.start.step3.body': 'This reads one contact. It changes nothing, so it is a safe way to check that the key works.',
  'developers.start.firstCall': 'First request',
  'developers.start.success': 'A successful answer',
  'developers.start.successNote': 'Your rows are in data. A list also carries meta, with the total and, where there is another page, a next_cursor to pass back as cursor.',
  'developers.start.failure': 'A failure',
  'developers.start.failureNote': 'Every failure has this shape. Match on error.code, which is stable. The message is written for a person and may be reworded.',
  'developers.start.scopes.title': 'Scopes',
  'developers.start.scopes.body': 'Each endpoint names the one scope a key must hold to call it. A key without it is refused, and the refusal says which scope was wanted.',
  'developers.start.scopes.fixed': 'Scopes are chosen when a key is created and cannot be changed afterwards. To widen a key, create a new one and revoke the old.',
  'developers.start.limits.title': 'Limits, paging and errors',
  'developers.start.limits.body': 'Requests are counted per key over a rolling minute. Every answer carries X-RateLimit-Limit and X-RateLimit-Remaining. Over the limit you get 429, with a Retry-After header saying how many seconds to wait. Sending messages has its own, larger allowance, so a burst of sends never eats into the rest of your integration.',
  'developers.start.pagination': 'Lists take limit and either offset or cursor. Read meta.next_cursor from the answer and pass it back as cursor for the next page. No next_cursor means you have reached the end.',
  'developers.start.errors': 'Failures always look the same: an error object with a code, a message, sometimes details, and a request_id worth quoting if you ask us about one.',
  'developers.start.promise.title': 'What you can rely on',
  'developers.start.promise.stable': 'Nothing is removed from v1 without notice, and no field changes meaning under you.',
  'developers.start.promise.additive': 'We may add new endpoints, new optional request fields and new response fields at any time. Parse leniently and ignore anything you do not recognise.',
  'developers.start.promise.removal': 'If an endpoint is ever to be withdrawn, it is first marked deprecated in this document, and it keeps working for at least six months after that.',
  'developers.start.promise.breaking': 'A breaking change would arrive as a new prefix, v2. It would never arrive as a changed v1.',
  'developers.filter.search': 'Search',
  'developers.filter.searchPlaceholder': 'contacts, POST, messages:send',
  'developers.filter.searchHint': 'Matches the path, the summary, the method and the scope.',
  'developers.filter.group': 'Group',
  'developers.filter.allGroups': 'All groups',
  'developers.filter.scope': 'Scope',
  'developers.filter.allScopes': 'All scopes',
  'developers.empty.title': 'Nothing to show',
  'developers.empty.noMatch': 'No endpoint matches those filters.',
  'developers.empty.noSpec': 'The endpoint list is empty, which means the documentation could not be assembled. Try again in a moment.',
  'developers.empty.clear': 'Clear the filters',
  'developers.op.deprecated': 'Deprecated',
  'developers.op.scopeNote': 'Why this scope: {{note}}',
  'developers.op.tab.reference': 'Reference',
  'developers.op.tab.curl': 'curl',
  'developers.op.tab.try': 'Try it',
  'developers.op.parameters': 'Parameters',
  'developers.op.noParameters': 'This endpoint takes no parameters.',
  'developers.op.requestBody': 'Request body',
  'developers.op.responses': 'Answers',
  'developers.op.curlWithKey': 'Your key is filled in. Anyone you share this command with can use it, so treat it as the credential it is.',
  'developers.op.curlNoKey': 'Paste your key above and this command becomes ready to run.',
  'developers.op.curlPlaceholders': 'The braces are placeholders. Fill them in under Try it, or replace them by hand.',
  'developers.op.fillIn': 'Fill in the values',
  'developers.schema.field': 'Field',
  'developers.schema.parameter': 'Name',
  'developers.schema.in': 'Sent in',
  'developers.schema.type': 'Type',
  'developers.schema.notes': 'Notes',
  'developers.schema.required': 'Required',
  'developers.schema.none': 'No body.',
  'developers.try.pathValues': 'Values in the address',
  'developers.try.pathHint': 'Replaces {{name}} in the address.',
  'developers.try.queryValues': 'Query parameters',
  'developers.try.queryHint': 'Left empty, it is not sent.',
  'developers.try.body': 'Request body',
  'developers.try.bodyHint': 'Filled in from the schema as a worked example. Edit it before sending.',
  'developers.try.badJson': 'This is not valid JSON.',
  'developers.try.needKey': 'Paste your API key above to send a request from this page.',
  'developers.try.liveTitle': 'This is a live request',
  'developers.try.liveBody': 'There is no test mode. This runs against your real account with your real key, and whatever it does is done.',
  'developers.try.confirm': 'I understand, arm it',
  'developers.try.confirmed': 'Armed. Send when ready.',
  'developers.try.deleteBody': 'Deleting cannot be undone. Type the id you are about to delete, exactly as it appears above.',
  'developers.try.deleteLabel': 'Type the id to confirm',
  'developers.try.deleteHint': 'Must match {{id}} exactly.',
  'developers.try.deleteNoTarget': 'Fill in the address first, then confirm it here.',
  'developers.try.send': 'Send the request',
  'developers.try.missing': 'Still to fill in: {{names}}',
  'developers.try.response': 'Answer',
  'developers.try.emptyBody': 'The answer had no body.',
  'developers.try.timing': '{{ms}} ms',
  'developers.try.remaining': '{{remaining}} of {{limit}} requests left this minute',
  'developers.try.blockedTitle': 'The browser would not send it',
  'developers.try.blockedBody': 'The request never left this page, so there is nothing to show. Either the API is unreachable from here, or this console\'s address is not one the API accepts browser requests from. The curl command works from a terminal either way, and it is what we would run to check.',
  'developers.try.blockedDetail': 'The browser said: {{reason}}',
  'developers.hooks.title': 'Events we send you',
  'developers.hooks.body': 'Rather than asking us repeatedly whether anything has happened, register an address and we post to it when it does.',
  'developers.hooks.registerNote': 'Addresses are registered in Settings, under Webhooks. The signing secret is shown once, when you add the address.',
  'developers.hooks.events': 'What we send',
  'developers.hooks.noEvents': 'No events are published in this version of the document.',
  'developers.hooks.headers': 'Headers on every delivery',
  'developers.hooks.header': 'Header',
  'developers.hooks.hEvent': 'Which event this is.',
  'developers.hooks.hDelivery': 'Unique to this attempt. Use it to ignore a delivery you have already handled.',
  'developers.hooks.hTimestamp': 'Unix seconds. Part of the signed string, and how you spot a stale delivery.',
  'developers.hooks.hSignature': 'sha256= followed by the signature, in lowercase hex.',
  'developers.hooks.verify': 'Checking a delivery is ours',
  'developers.hooks.verifyBody': 'Sign the timestamp and the raw body together with your secret, and compare. Do this before you trust anything in the body: an address you publish is an address anyone can post to.',
  'developers.hooks.retries': 'Answer 2xx to accept. A 4xx other than 408 and 429 is taken as a permanent refusal and is not retried; anything else is retried with a growing gap. Deliveries are at least once, so make your handler safe to run twice on the same delivery id.',
  'developers.changelog.current': 'Current',
  'developers.changelog.empty': 'Nothing has been published yet.',
  'developers.changelog.kind.added': 'Added',
  'developers.changelog.kind.changed': 'Changed',
  'developers.changelog.kind.fixed': 'Fixed',
  'developers.changelog.kind.deprecated': 'Deprecated',
  'developers.notHere.title': 'Something you need is not here?',
  'developers.notHere.body': 'This page lists exactly what an API key can reach. If an endpoint you need is missing, it is because no key scope covers it yet rather than because it was forgotten. Tell us what you are building and we will look at the scope it would need.',
  'developers.notHere.contact': 'Email support'
};

export default developers;
